// Templates — email + SMS template editor.
//
// Templates were Payload-managed; this surface gives operators a direct
// editor backed by `/admin/templates` so they can update wording without
// dropping into the CMS. Each template carries 4 locale variants
// (en / fr / ar / es); the editor binds to the selected locale and the
// save call ships only the locales that were dirtied.
//
// Three modes per template: Edit (form), Preview (rendered HTML iframe),
// Test send (real send via SMTP/Twilio).

import { useEffect, useMemo, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { PageHeader } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchAdminTemplatesIndex,
  fetchEmailTemplate,
  fetchSmsTemplate,
  previewEmailTemplate,
  previewSmsTemplate,
  saveEmailTemplate,
  saveSmsTemplate,
  sendEmailTemplateTest,
  sendSmsTemplateTest,
  type EmailLocaleFields,
  type EmailTemplateRecord,
  type EmailTemplateType,
  type RenderedEmail,
  type RenderedSms,
  type SmsLocaleFields,
  type SmsTemplateRecord,
  type SmsTemplateType,
  type TemplateLocale,
} from '@/lib/admin-api';

type Channel = 'email' | 'sms';
type Mode = 'edit' | 'preview' | 'test';

type RenderedAny = RenderedEmail | RenderedSms;
function isEmailPreview(r: RenderedAny): r is RenderedEmail {
  return 'subject' in r;
}

interface Selection {
  channel: Channel;
  type: string;
}

const LOCALES: TemplateLocale[] = ['en', 'fr', 'ar', 'es'];

const LOCALE_LABEL: Record<TemplateLocale, string> = {
  en: 'English',
  fr: 'Français',
  ar: 'العربية',
  es: 'Español',
};

function humanizeType(type: string): string {
  return type.replace(/-/g, ' ');
}

function emptyEmailLocale(): EmailLocaleFields {
  return { subject: '', preheader: '', bodyHtml: '', bodyText: '' };
}

function emptySmsLocale(): SmsLocaleFields {
  return { body: '' };
}

export function Templates() {
  const indexQ = useQuery({
    queryKey: ['admin', 'templates', 'index'],
    queryFn: ({ signal }) => fetchAdminTemplatesIndex(signal),
  });

  const [selection, setSelection] = useState<Selection | null>(null);
  const [locale, setLocale] = useState<TemplateLocale>('en');
  const [mode, setMode] = useState<Mode>('edit');
  const [testRecipient, setTestRecipient] = useState('');

  // Auto-select the first template once the index loads.
  const firstSelection = useMemo<Selection | null>(() => {
    if (!indexQ.data) return null;
    const e = indexQ.data.email[0];
    if (e) return { channel: 'email', type: e.type };
    const s = indexQ.data.sms[0];
    if (s) return { channel: 'sms', type: s.type };
    return null;
  }, [indexQ.data]);
  if (!selection && firstSelection) setSelection(firstSelection);

  const email = indexQ.data?.email ?? [];
  const sms = indexQ.data?.sms ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Templates"
        sub="Edit, preview, and test the email + SMS templates that power booking and inquiry notifications. Changes apply on the next outbound message — no deploy needed."
      />
      {indexQ.error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load template index: {(indexQ.error as Error).message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <aside className="rounded-xl border border-[color:var(--bo-line)] bg-white p-3">
          <Group
            title="Email"
            items={email.map((e) => e.type)}
            channel="email"
            selection={selection}
            onSelect={(type) => {
              setSelection({ channel: 'email', type });
              setMode('edit');
            }}
          />
          <div className="mt-4">
            <Group
              title="SMS"
              items={sms.map((s) => s.type)}
              channel="sms"
              selection={selection}
              onSelect={(type) => {
                setSelection({ channel: 'sms', type });
                setMode('edit');
              }}
            />
          </div>
        </aside>

        <div className="space-y-4">
          {selection ? (
            <TemplateWorkspace
              key={`${selection.channel}-${selection.type}`}
              selection={selection}
              locale={locale}
              onLocale={setLocale}
              mode={mode}
              onMode={setMode}
              testRecipient={testRecipient}
              onTestRecipient={setTestRecipient}
            />
          ) : (
            <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
              {indexQ.isLoading ? 'Loading…' : 'Select a template to edit.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Workspace (per template) ────────────────────────────────────────

function TemplateWorkspace({
  selection,
  locale,
  onLocale,
  mode,
  onMode,
  testRecipient,
  onTestRecipient,
}: {
  selection: Selection;
  locale: TemplateLocale;
  onLocale: (l: TemplateLocale) => void;
  mode: Mode;
  onMode: (m: Mode) => void;
  testRecipient: string;
  onTestRecipient: (s: string) => void;
}) {
  const isEmail = selection.channel === 'email';
  const queryClient = useQueryClient();

  // ─── Load
  type AnyRecord = EmailTemplateRecord | SmsTemplateRecord;
  const recordQ = useQuery<AnyRecord, Error>({
    queryKey: ['admin', 'templates', 'record', selection.channel, selection.type],
    queryFn: ({ signal }) =>
      isEmail
        ? fetchEmailTemplate(selection.type as EmailTemplateType, signal)
        : fetchSmsTemplate(selection.type as SmsTemplateType, signal),
  });

  // ─── Local draft state
  const [draft, setDraft] = useState<EmailTemplateRecord | SmsTemplateRecord | null>(
    null,
  );
  useEffect(() => {
    if (recordQ.data) setDraft(recordQ.data);
  }, [recordQ.data]);

  const dirty = useMemo(() => {
    if (!draft || !recordQ.data) return false;
    if (draft.enabled !== recordQ.data.enabled) return true;
    if (draft.name !== recordQ.data.name) return true;
    for (const l of LOCALES) {
      const a = draft.locales[l];
      const b = recordQ.data.locales[l];
      if (JSON.stringify(a) !== JSON.stringify(b)) return true;
    }
    return false;
  }, [draft, recordQ.data]);

  // ─── Save
  const saveMut = useMutation({
    mutationFn: () => {
      if (!draft || !recordQ.data) throw new Error('Nothing to save');
      // Only ship locales that actually changed.
      const localesPayload: Record<string, Record<string, string>> = {};
      for (const l of LOCALES) {
        if (JSON.stringify(draft.locales[l]) !== JSON.stringify(recordQ.data.locales[l])) {
          localesPayload[l] = draft.locales[l] as unknown as Record<string, string>;
        }
      }
      if (isEmail) {
        return saveEmailTemplate(selection.type as EmailTemplateType, {
          enabled: draft.enabled,
          name: draft.name,
          locales: localesPayload as unknown as Partial<
            Record<TemplateLocale, Partial<EmailLocaleFields>>
          >,
        });
      }
      return saveSmsTemplate(selection.type as SmsTemplateType, {
        enabled: draft.enabled,
        name: draft.name,
        locales: localesPayload as unknown as Partial<
          Record<TemplateLocale, Partial<SmsLocaleFields>>
        >,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          'admin',
          'templates',
          'record',
          selection.channel,
          selection.type,
        ],
      });
      // Preview cache is stale after a save — drop it so re-opening Preview
      // re-renders against the new content.
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'templates', 'preview', selection],
      });
    },
  });

  // ─── Preview (only when mode='preview')
  const previewQ = useQuery<RenderedAny, Error>({
    queryKey: ['admin', 'templates', 'preview', selection, locale],
    queryFn: () =>
      isEmail
        ? previewEmailTemplate(selection.type as EmailTemplateType, locale)
        : previewSmsTemplate(selection.type as SmsTemplateType, locale),
    enabled: mode === 'preview',
  });

  // ─── Test send (only on demand)
  const sendMut = useMutation({
    mutationFn: () => {
      const to = testRecipient.trim();
      if (!to) throw new Error('Enter a recipient');
      return isEmail
        ? sendEmailTemplateTest(selection.type as EmailTemplateType, to, locale)
        : sendSmsTemplateTest(selection.type as SmsTemplateType, to, locale);
    },
  });

  if (recordQ.isLoading || !draft) {
    return (
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
        Loading template…
      </div>
    );
  }
  if (recordQ.error) {
    return (
      <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
        Couldn&apos;t load: {(recordQ.error as Error).message}
      </div>
    );
  }

  const updateLocale = (
    next: Partial<EmailLocaleFields & SmsLocaleFields>,
  ) => {
    setDraft((d) => {
      if (!d) return d;
      return {
        ...d,
        locales: {
          ...d.locales,
          [locale]: { ...(d.locales[locale] as object), ...next },
        },
      } as typeof d;
    });
  };

  return (
    <>
      <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
              {isEmail ? 'Email template' : 'SMS template'}
            </div>
            <div className="text-[16px] font-semibold capitalize tracking-tight">
              {humanizeType(selection.type)}
            </div>
            {!draft.exists ? (
              <div className="mt-1 text-[11.5px] text-[#854d0e]">
                Not yet seeded — first save will create the row.
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-[12px]">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
                className="h-3.5 w-3.5 accent-[color:var(--bo-accent)]"
              />
              Enabled
            </label>
            <div className="flex items-center gap-1 rounded-lg border border-[color:var(--bo-line)] bg-white p-0.5">
              {(['edit', 'preview', 'test'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => onMode(m)}
                  className={`rounded-md px-2.5 py-1 text-[12px] font-medium capitalize ${
                    mode === m
                      ? 'bg-[color:var(--bo-ink)] text-white'
                      : 'text-[color:var(--bo-ink)] hover:bg-[color:var(--bo-bg)]'
                  }`}
                >
                  {m === 'test' ? 'Test send' : m}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1">
          {LOCALES.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => onLocale(l)}
              className={`rounded-md border px-2 py-1 text-[12px] font-medium ${
                locale === l
                  ? 'border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] text-white'
                  : 'border-[color:var(--bo-line)] bg-white text-[color:var(--bo-ink)] hover:border-[color:var(--bo-mute)]'
              }`}
            >
              {LOCALE_LABEL[l]}
            </button>
          ))}
        </div>
      </div>

      {mode === 'edit' ? (
        isEmail ? (
          <EmailEditor
            fields={(draft as EmailTemplateRecord).locales[locale] ?? emptyEmailLocale()}
            onChange={updateLocale}
          />
        ) : (
          <SmsEditor
            fields={(draft as SmsTemplateRecord).locales[locale] ?? emptySmsLocale()}
            onChange={updateLocale}
          />
        )
      ) : null}

      {mode === 'preview' ? (
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          {previewQ.isLoading ? (
            <div className="text-[12.5px] text-[color:var(--bo-mute)]">
              Rendering preview…
            </div>
          ) : previewQ.error ? (
            <div className="text-[12.5px] text-[#9f1239]">
              Couldn&apos;t render: {(previewQ.error as Error).message}
            </div>
          ) : previewQ.data && isEmailPreview(previewQ.data) ? (
            <EmailPreview rendered={previewQ.data} />
          ) : previewQ.data ? (
            <SmsPreview rendered={previewQ.data} />
          ) : null}
          <p className="mt-3 text-[11px] text-[color:var(--bo-mute)]">
            Preview uses an example context (sample booking + customer + vendor).
            Save your edits before previewing if you want to see the new copy.
          </p>
        </div>
      ) : null}

      {mode === 'test' ? (
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
          <div className="mb-3 text-[13px] font-semibold">Send a test</div>
          <p className="mb-3 text-[11.5px] text-[color:var(--bo-mute)]">
            Renders the template with the example context and sends via the
            real {isEmail ? 'SMTP' : 'Twilio'} transport, prefixed with{' '}
            <code className="font-mono">[TEST]</code>. Test sends use the
            currently-saved copy — save your edits first.
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
            <input
              type={isEmail ? 'email' : 'tel'}
              value={testRecipient}
              onChange={(e) => onTestRecipient(e.target.value)}
              placeholder={isEmail ? 'name@example.com' : '+15555550199'}
              className="h-9 rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
            />
            <RowAction
              kind="primary"
              busy={sendMut.isPending}
              disabled={!testRecipient.trim() || sendMut.isPending}
              onClick={() => sendMut.mutate()}
            >
              Send test {isEmail ? 'email' : 'SMS'}
            </RowAction>
          </div>
          {sendMut.error ? (
            <div className="mt-2 text-[12px] text-[#9f1239]">
              Send failed: {(sendMut.error as Error).message}
            </div>
          ) : null}
          {sendMut.isSuccess && !sendMut.isPending ? (
            <div className="mt-2 text-[12px] text-[#166534]">
              Sent. Check {isEmail ? 'inbox + spam folder' : 'phone'}.
            </div>
          ) : null}
        </div>
      ) : null}

      <SaveBar
        dirty={dirty}
        saving={saveMut.isPending}
        success={saveMut.isSuccess && !dirty}
        error={saveMut.error}
        onReset={() => setDraft(recordQ.data ?? null)}
        onSave={() => saveMut.mutate()}
      />
    </>
  );
}

// ─── Editors ─────────────────────────────────────────────────────────

function EmailEditor({
  fields,
  onChange,
}: {
  fields: EmailLocaleFields;
  onChange: (next: Partial<EmailLocaleFields>) => void;
}) {
  return (
    <div className="space-y-4 rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Subject
        </label>
        <input
          type="text"
          value={fields.subject}
          onChange={(e) => onChange({ subject: e.target.value })}
          className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
      </div>
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Preheader
        </label>
        <input
          type="text"
          value={fields.preheader}
          onChange={(e) => onChange({ preheader: e.target.value })}
          placeholder="Short snippet shown by inbox previews (optional)"
          className="h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
      </div>
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          HTML body
        </label>
        <textarea
          value={fields.bodyHtml}
          onChange={(e) => onChange({ bodyHtml: e.target.value })}
          rows={18}
          spellCheck={false}
          className="block w-full resize-y rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 font-mono text-[12px] leading-relaxed text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
        <p className="mt-1 text-[11px] text-[color:var(--bo-mute)]">
          Supports <code className="font-mono">{'{{var.path}}'}</code>{' '}
          interpolation — e.g.{' '}
          <code className="font-mono">{'{{customer.firstName}}'}</code>,{' '}
          <code className="font-mono">{'{{booking.reference}}'}</code>,{' '}
          <code className="font-mono">{'{{car.title}}'}</code>.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Plain-text fallback
        </label>
        <textarea
          value={fields.bodyText}
          onChange={(e) => onChange({ bodyText: e.target.value })}
          rows={6}
          placeholder="Optional plain-text version for non-HTML clients."
          className="block w-full resize-y rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
      </div>
    </div>
  );
}

function SmsEditor({
  fields,
  onChange,
}: {
  fields: SmsLocaleFields;
  onChange: (next: Partial<SmsLocaleFields>) => void;
}) {
  return (
    <div className="space-y-3 rounded-xl border border-[color:var(--bo-line)] bg-white p-5">
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          SMS body
        </label>
        <textarea
          value={fields.body}
          onChange={(e) => onChange({ body: e.target.value })}
          rows={6}
          className="block w-full resize-y rounded-lg border border-[color:var(--bo-line)] bg-white px-3 py-2 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
        <div className="mt-1 flex items-center justify-between text-[11px] text-[color:var(--bo-mute)]">
          <span>
            Supports <code className="font-mono">{'{{var.path}}'}</code>{' '}
            interpolation, same as email.
          </span>
          <span className="tabular-nums">
            {fields.body.length} chars · ~
            {Math.ceil(fields.body.length / 160) || 1} segment(s)
          </span>
        </div>
      </div>
    </div>
  );
}

function EmailPreview({ rendered }: { rendered: RenderedEmail }) {
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Subject
        </div>
        <div className="mt-1 text-[14px] font-semibold">{rendered.subject}</div>
      </div>
      <div>
        <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          HTML preview
        </div>
        <iframe
          title="Email preview"
          srcDoc={rendered.html}
          sandbox="allow-popups allow-same-origin"
          className="h-[480px] w-full rounded-lg border border-[color:var(--bo-line)] bg-white"
        />
      </div>
      {rendered.text ? (
        <div>
          <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
            Plain-text fallback
          </div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-[color:var(--bo-bg)] p-3 text-[12px]">
            {rendered.text}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function SmsPreview({ rendered }: { rendered: RenderedSms }) {
  return (
    <div>
      <div className="mb-1 text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
        SMS body
      </div>
      <div className="rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] p-4 text-[14px] leading-relaxed">
        {rendered.body}
      </div>
      <div className="mt-2 text-[11.5px] text-[color:var(--bo-mute)]">
        {rendered.body.length} chars · ~
        {Math.ceil(rendered.body.length / 160) || 1} segment(s)
      </div>
    </div>
  );
}

function SaveBar({
  dirty,
  saving,
  success,
  error,
  onSave,
  onReset,
}: {
  dirty: boolean;
  saving: boolean;
  success: boolean;
  error: unknown;
  onSave: () => void;
  onReset: () => void;
}) {
  return (
    <div className="sticky bottom-2 z-10 -mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--bo-line)] bg-white px-4 py-2.5 shadow-sm">
      <div className="text-[12.5px] text-[color:var(--bo-mute)]">
        {dirty ? (
          <span className="font-medium text-[color:var(--bo-ink)]">Unsaved changes</span>
        ) : success ? (
          <span className="text-[#166534]">Saved.</span>
        ) : (
          <span>Up to date.</span>
        )}
        {error ? (
          <span className="ms-3 text-[#9f1239]">Save failed: {(error as Error).message}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg border border-[color:var(--bo-line)] px-3 text-[12.5px] font-medium text-[color:var(--bo-ink)] transition hover:border-[color:var(--bo-mute)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center rounded-lg bg-[color:var(--bo-ink)] px-3.5 text-[12.5px] font-semibold text-white transition hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Sidebar group ───────────────────────────────────────────────────

function Group({
  title,
  items,
  channel,
  selection,
  onSelect,
}: {
  title: string;
  items: string[];
  channel: Channel;
  selection: Selection | null;
  onSelect: (type: string) => void;
}) {
  return (
    <div>
      <div className="px-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--bo-mute)]">
        {title}
      </div>
      <ul>
        {items.map((type) => {
          const isActive =
            selection?.channel === channel && selection.type === type;
          return (
            <li key={`${channel}-${type}`}>
              <button
                type="button"
                onClick={() => onSelect(type)}
                className={`w-full rounded-md px-2 py-1.5 text-start text-[12.5px] capitalize transition ${
                  isActive
                    ? 'bg-[color:var(--bo-bg)] font-semibold text-[color:var(--bo-ink)]'
                    : 'text-[color:var(--bo-ink)]/80 hover:bg-[color:var(--bo-bg)]'
                }`}
              >
                {humanizeType(type)}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
